import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import {
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Button,
  Selection,
  useDisclosure,
  Skeleton,
  addToast,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@heroui/react";
import { RiSearch2Line, RiGitMergeLine, RiMailSendLine } from "react-icons/ri";
import { api } from "@/lib/api-client";
import { MdBlock } from "react-icons/md";
import dayjs from "dayjs";
import useFeature from "../../../hooks/useFeature";
import CustomerMergeModal from "./CustomerMergeModal";
import { BulkSMSModal } from "./BulkSMSModal";
import { PostMergeCustomers } from "@/server-types";
import { LimePagination } from "@/Components/NextBase/LimePagination";
import { Settings2 } from "lucide-react";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { useSearchParams } from "react-router";
import { GetCustomerList } from "@/server-types";
import { useDebouncedCallback } from "@mantine/hooks";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";

type RowCustomer = {
  id: number;
  key: string;
  name: string;
  lastName: string;
  email: string;
  gsm: string;
  startTime: string;
  formBlocked: boolean;
};

const ROWS_PER_PAGE_OPTIONS = ["10", "25", "50", "100"];

const CustomerList = ({
  onClickCustomer,
}: {
  onClickCustomer: (customerId: number) => void;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageParam = searchParams.get("page");
  const perPageParam = searchParams.get("perPage");
  const totalPagesParam = searchParams.get("totalPages");
  const sortByParam = searchParams.get("sortBy");
  const searchParam = searchParams.get("q");

  const page = pageParam ? Number(pageParam) : 1;
  const perPage = perPageParam
    ? Number(perPageParam)
    : Number(ROWS_PER_PAGE_OPTIONS[0]);
  const totalPages = totalPagesParam ? Number(totalPagesParam) : 1;
  const sortBy = sortByParam
    ? (sortByParam as GetCustomerList["query"]["sortBy"])
    : "nameAsc";

  const selectedLocationsParam = searchParams.getAll("loc");
  const selectedLabelsParam = searchParams.getAll("lab");
  const selectedServicesParam = searchParams.getAll("serv");
  const showWithoutAppointmentsParam = searchParams.get("noAppts");

  const showWithoutAppointments =
    showWithoutAppointmentsParam != null
      ? showWithoutAppointmentsParam === "true"
      : true;

  const [selectedCustomerIds, setSelectedCustomerIds] = useState<Set<number>>(
    new Set(),
  );

  const setSearchDebounced = useDebouncedCallback((query: string) => {
    setSearchParams((prev) => {
      if (query === "") {
        prev.delete("q");
      } else {
        prev.set("q", query);
      }
      return prev;
    });
  }, 400);
  const handleSearchChange = (val: string) => {
    setSearchDebounced(val);
  };

  const {
    isOpen: isMergeModalOpen,
    onOpen: onMergeModalOpen,
    onClose: onMergeModalClose,
  } = useDisclosure();

  const {
    isOpen: isBulkSMSModalOpen,
    onOpen: onBulkSMSModalOpen,
    onClose: onBulkSMSModalClose,
  } = useDisclosure();

  const {
    data: locationsData,
    isFetching: isFetchingLocations,
    processedErrorMessage: fetchLocationsErrorMessage,
  } = api.location.useGetLocations({
    sortBy: "labelAsc",
  });
  const {
    data: labelsData,
    isFetching: isFetchingLabels,
    processedErrorMessage: fetchLabelsErrorMessage,
  } = api.customer.useGetCustomerLabels();
  const {
    data: servicesData,
    isFetching: isFetchingServices,
    processedErrorMessage: fetchServicesErrorMessage,
  } = api.service.useGetServices({});

  const selectedLocations = selectedLocationsParam.length
    ? new Set(selectedLocationsParam).values().toArray().map(Number)
    : locationsData?.locations.map((loc) => loc.locationId) || [];
  const selectedLabels = selectedLabelsParam.length
    ? new Set(selectedLabelsParam).values().toArray()
    : ["noLabel", ...(labelsData?.map((label) => label.id.toString()) || [])];
  const selectedServices = selectedServicesParam.length
    ? new Set(selectedServicesParam).values().toArray().map(Number)
    : servicesData?.services.map((service) => service.serviceId) || [];

  const {
    data,
    isLoading,
    processedErrorMessage: fetchCustomerListErrorMessage,
  } = api.appointments.useCustomerList({
    page,
    perPage,
    sortBy,
    query: searchParam ?? undefined,
    locations: Array.from(selectedLocations).map(Number),
    labels: Array.from(selectedLabels).map(Number).filter(Boolean),
    services: Array.from(selectedServices).map(Number),
    includeCustomersWithoutAppointments: showWithoutAppointments,
    includeCustomersWithoutLabels:
      Array.from(selectedLabels).includes("noLabel"),
  });

  const setSelectedLocations = (keys: Selection) => {
    setSearchParams((prev) => {
      prev.delete("loc");
      Array.from(keys).forEach((key) => {
        prev.append("loc", String(key));
      });
      return prev;
    });
  };

  const setSelectedLabels = (keys: Selection) => {
    setSearchParams((prev) => {
      prev.delete("lab");
      Array.from(keys).forEach((key) => {
        prev.append("lab", String(key));
      });
      return prev;
    });
  };

  const setSelectedServices = (keys: Selection) => {
    setSearchParams((prev) => {
      prev.delete("serv");
      Array.from(keys).forEach((key) => {
        prev.append("serv", String(key));
      });
      return prev;
    });
  };

  const { isFeatureEnabled } = useFeature();

  // needed for smooth pagination
  useEffect(() => {
    if (data?.pagination.pageCount) {
      const newTotalPages = Math.max(1, data.pagination.pageCount);
      if (newTotalPages !== totalPages) {
        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set("totalPages", newTotalPages.toString());
            if (page > newTotalPages) {
              newParams.set("page", "1");
            }
            return newParams;
          },
          { replace: true },
        );
      }
    }
  }, [data]);

  const handleSelectionChange = (keys: Selection) => {
    if (keys === "all") {
      const allCustomerIds =
        data?.customers.map((customer) => customer.customerId) || [];
      setSelectedCustomerIds(new Set(allCustomerIds));
    } else {
      const keysArray = Array.from(keys) as number[];
      setSelectedCustomerIds(new Set(keysArray));
    }
  };

  const { mutateAsync: mergeCustomers } = api.customer.usePostMergeCustomers();
  const handleMergeSubmit = async (values: PostMergeCustomers["body"]) => {
    try {
      await mergeCustomers(values);
      addToast({
        title: t`Uspešno združeno`,
        description: t`Stranke so bile uspešno združene`,
        color: "success",
      });
      onMergeModalClose();
      setSelectedCustomerIds(new Set());
    } catch (error) {
      addToast({
        title: t`Napaka`,
        description: t`Pri združevanju strank je prišlo do napake`,
        color: "danger",
      });
    }
  };

  const topContent = useMemo(() => {
    return (
      <div className="mt-6 flex flex-col gap-4 sm:flex-row">
        <div className="flex w-full flex-row justify-start gap-2 sm:w-auto">
          <LimeInput
            isClearable
            className="w-full sm:min-w-[300px] sm:max-w-[300px]"
            placeholder={t`Išči po imenu...`}
            autoFocus
            startContent={<RiSearch2Line />}
            defaultValue={searchParam}
            onClear={() => {
              setSearchParams((prev) => {
                prev.delete("q");
                return prev;
              });
            }}
            onValueChange={handleSearchChange}
          />
          <Popover className="w-[500px]">
            <PopoverTrigger>
              <Button
                isIconOnly
                variant="flat"
                startContent={<Settings2 className="h-5 w-5" />}
              />
            </PopoverTrigger>
            <PopoverContent
              aria-label="Nastavitve prikaza"
              className="min-w-[320px]"
            >
              <div className="flex w-full flex-col gap-2 py-3">
                <LimeSelect
                  label={t`Razvrsti po`}
                  labelPlacement="outside"
                  className="w-full"
                  selectedKeys={new Set([sortBy])}
                  onSelectionChange={(keys) => {
                    const val = Array.from(
                      keys,
                    )[0] as GetCustomerList["query"]["sortBy"];
                    setSearchParams((prev) => {
                      prev.set("sortBy", val);
                      return prev;
                    });
                  }}
                  items={[
                    {
                      key: "nameAsc",
                      label: t`Imenu A-Z`,
                    },
                    {
                      key: "nameDesc",
                      label: t`Imenu Z-A`,
                    },
                    {
                      key: "lastNameAsc",
                      label: t`Priimku A-Z`,
                    },
                    {
                      key: "lastNameDesc",
                      label: t`Priimku Z-A`,
                    },
                    {
                      key: "lastVisitDesc",
                      label: t`Datumu zadnjega obiska (novejši najprej)`,
                    },
                    {
                      key: "lastVisitAsc",
                      label: t`Datumu zadnjega obiska (starejši najprej)`,
                    },
                  ]}
                ></LimeSelect>

                <LimeSelect
                  label={t`Lokacije`}
                  labelPlacement="outside"
                  className="w-full"
                  placeholder={t`Izberi lokacije`}
                  selectionMode="multiple"
                  selectedKeys={selectedLocations.map(String)}
                  onSelectionChange={setSelectedLocations}
                  isLoading={isFetchingLocations}
                  error={fetchLocationsErrorMessage}
                  renderValue={(items) => {
                    if (items.length === 0) return t`Izberi lokacije`;
                    if (items.length === locationsData?.locations.length)
                      return t`Vse lokacije`;
                    if (items.length > 2)
                      return `${items.length} ${t`lokacije`}`;
                    return items.map((item) => item.rendered).join(", ");
                  }}
                  items={
                    locationsData?.locations.map((loc) => ({
                      key: String(loc.locationId),
                      label: loc.label,
                    })) ?? []
                  }
                ></LimeSelect>

                <LimeSelect
                  label={t`Oznake strank`}
                  labelPlacement="outside"
                  className="w-full"
                  placeholder={t`Izberi oznake`}
                  selectionMode="multiple"
                  selectedKeys={selectedLabels.map(String)}
                  onSelectionChange={setSelectedLabels}
                  isLoading={isFetchingLabels}
                  error={fetchLabelsErrorMessage}
                  renderValue={(items) => {
                    if (items.length === 0) return t`Izberi oznake`;
                    if (items.length >= (labelsData?.length || 0))
                      return t`Vse oznake`;
                    if (items.length > 2) return `${items.length} ${t`oznake`}`;
                    return items.map((item) => item.rendered).join(", ");
                  }}
                  items={[
                    {
                      key: "noLabel",
                      label: t`Brez oznak`,
                    },
                    ...(labelsData || []).map((label) => ({
                      key: String(label.id),
                      label: label.name,
                    })),
                  ]}
                ></LimeSelect>

                <LimeSelect
                  label={t`Storitve`}
                  labelPlacement="outside"
                  className="w-full"
                  placeholder={t`Izberi storitve`}
                  selectionMode="multiple"
                  selectedKeys={selectedServices.map(String)}
                  onSelectionChange={setSelectedServices}
                  isLoading={isFetchingServices}
                  error={fetchServicesErrorMessage}
                  renderValue={(items) => {
                    if (items.length === 0) return t`Izberi storitve`;
                    if (items.length === servicesData?.services.length)
                      return t`Vse storitve`;
                    if (items.length > 2)
                      return `${items.length} ${t`storitev`}`;
                    return items.map((item) => item.rendered).join(", ");
                  }}
                  items={
                    servicesData?.services.map((service) => ({
                      key: String(service.serviceId),
                      label: service.name,
                    })) ?? []
                  }
                ></LimeSelect>

                <div className="flex items-center gap-2">
                  <LimeSwitch
                    checked={showWithoutAppointments}
                    onChange={(val: boolean) => {
                      setSearchParams((prev) => {
                        prev.set("noAppts", val.toString());
                        return prev;
                      });
                    }}
                    size="sm"
                    title={t`Prikaži tudi stranke brez obiska`}
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        {Array.from(selectedCustomerIds).length > 0 && (
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              onPress={onMergeModalOpen}
              startContent={<RiGitMergeLine />}
              variant="flat"
            >
              <Trans>Združi izbrane</Trans>
              {" ("}
              {Array.from(selectedCustomerIds).length}
              {")"}
            </Button>
            {isFeatureEnabled("BULK_NOTIF") && (
              <Button
                onPress={onBulkSMSModalOpen}
                startContent={<RiMailSendLine />}
              >
                <Trans>Pošlji SMS</Trans>
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }, [
    isFeatureEnabled,
    onBulkSMSModalOpen,
    onMergeModalOpen,
    searchParam,
    selectedCustomerIds,
    data,
    showWithoutAppointments,
    selectedServices,
    selectedLocations,
    selectedLabels,
    servicesData,
    locationsData,
    labelsData,
  ]);

  const columns = [
    { key: "name", label: t`Ime`, alwaysVisible: true },
    { key: "lastName", label: t`Priimek`, alwaysVisible: true },
    { key: "email", label: t`Email` },
    { key: "gsm", label: t`Telefon` },
    { key: "startTime", label: t`Zadnji obisk` },
  ];

  const rows: RowCustomer[] = isLoading
    ? Array(perPage)
        .fill(null)
        .map((_, index) => ({
          id: index,
          key: `loading-${index}`,
          name: "Loading...",
          lastName: "Loading...",
          email: "Loading...",
          gsm: "Loading...",
          startTime: "Loading...",
          formBlocked: false,
        }))
    : data?.customers.map((customer) => ({
        id: customer.customerId,
        key: customer.customerId.toString(),
        name: customer.name,
        lastName: customer.lastName,
        email: customer.email || "",
        gsm: customer.gsm || "",
        startTime: customer.startTime
          ? dayjs(customer.startTime).utc(false).format("DD. MM. YYYY HH:mm")
          : t`Še ni obiska`,
        formBlocked: customer.formBlocked,
      })) || [];

  const renderCell = useCallback((item: RowCustomer, columnKey: React.Key) => {
    const cellValue = item[columnKey as keyof RowCustomer];

    switch (columnKey) {
      case "gsm":
        return (
          <>
            {item.gsm}
            {item.gsm && item.formBlocked && (
              <MdBlock
                title={t`Telefonska številka blokirana na spletnem naročanju`}
              />
            )}
          </>
        );
      default:
        return cellValue;
    }
  }, []);

  return (
    <>
      <CustomerMergeModal
        customers={
          data?.customers.filter((customer) =>
            selectedCustomerIds.has(customer.customerId),
          ) || []
        }
        isOpen={isMergeModalOpen}
        onOpenChange={onMergeModalClose}
        onMerge={handleMergeSubmit}
      />

      <BulkSMSModal
        isOpen={isBulkSMSModalOpen}
        onOpenChange={(isOpen) =>
          isOpen ? onBulkSMSModalOpen() : onBulkSMSModalClose()
        }
        selectedCustomerIds={Array.from(selectedCustomerIds)}
      />

      <LimeAlert message={fetchCustomerListErrorMessage} className="mt-2" />

      <Table
        aria-label="Customers table"
        topContent={topContent}
        topContentPlacement="outside"
        selectionMode="multiple"
        selectionBehavior="toggle"
        selectedKeys={selectedCustomerIds}
        onSelectionChange={handleSelectionChange}
        className="px-1 md:px-0"
        onRowAction={(key) => onClickCustomer(Number(key))}
      >
        <TableHeader>
          {columns.map((column) => (
            <TableColumn
              key={column.key}
              className={
                column.alwaysVisible ? undefined : "hidden sm:table-cell"
              }
            >
              {column.label}
            </TableColumn>
          ))}
        </TableHeader>
        <TableBody
          items={rows}
          emptyContent={isLoading ? undefined : t`Ni strank`}
        >
          {(item) => (
            <TableRow key={item.key}>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  className={
                    column.alwaysVisible ? undefined : "hidden sm:table-cell"
                  }
                >
                  <Skeleton isLoaded={!isLoading} className="rounded-xl">
                    {renderCell(item, column.key)}
                  </Skeleton>
                </TableCell>
              ))}
            </TableRow>
          )}
        </TableBody>
      </Table>
      <LimePagination
        className="mt-4"
        paginationProps={{
          total: totalPages,
          page,
          onChange: (newPage: number) => {
            setSearchParams((prev) => {
              prev.set("page", newPage.toString());
              return prev;
            });
          },
        }}
        perPageProps={{
          label: t`strank`,
          values: ROWS_PER_PAGE_OPTIONS.map(Number),
          onChange: (value: string) => {
            setSearchParams((prev) => {
              prev.set("perPage", value);
              prev.set("page", "1");
              return prev;
            });
          },
          value: perPage.toString(),
        }}
      />
    </>
  );
};

export default CustomerList;
